import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ImportFromPrivateWatchlistModal — Round 84d (Feb 14, 2026).
 *
 * Opens from inside the New Watchlist editor. Loads the user's
 * PRIVATE DiviDrip watchlist (the /api/portfolio rows), lets them
 * checkbox up to `maxSelectable` tickers, and returns the selection
 * via `onImport(tickers[])`. The parent editor then pre-fills the
 * ticker chips.
 *
 * `usedAcrossOtherLists` is the union of tickers already in the
 * user's OTHER community watchlists — those rows are rendered
 * disabled with a "Already in another list" hint, so the import
 * never produces a guaranteed-to-400 ticker set.
 */
export default function ImportFromPrivateWatchlistModal({
  open,
  onClose,
  onImport,
  maxSelectable = 5,
  alreadyInThisList = [],
  usedAcrossOtherLists = [],
}) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    setSelected(new Set());
    axios
      .get(`${API}/portfolio`, { withCredentials: true })
      .then((r) => {
        // /api/portfolio returns [{ticker, added_at}, ...]
        setItems((r.data || []).map((row) => row.ticker).filter(Boolean));
      })
      .catch(() => {
        setItems([]);
        toast.error('Could not load your private watchlist.');
      })
      .finally(() => setLoading(false));
    return undefined;
  }, [open]);

  if (!open) return null;

  const usedSet = new Set((usedAcrossOtherLists || []).map((t) => t.toUpperCase()));
  const alreadyHere = new Set((alreadyInThisList || []).map((t) => t.toUpperCase()));

  const toggle = (t) => {
    const next = new Set(selected);
    if (next.has(t)) {
      next.delete(t);
    } else if (next.size >= maxSelectable) {
      toast.error(`You can select up to ${maxSelectable} tickers.`);
      return;
    } else {
      next.add(t);
    }
    setSelected(next);
  };

  const handleImport = () => {
    if (selected.size === 0) {
      toast.error('Pick at least one ticker.');
      return;
    }
    onImport(Array.from(selected));
    onClose();
  };

  // Allow remaining = max - already-in-this-list
  const remaining = Math.max(0, maxSelectable - alreadyHere.size);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4"
      onClick={onClose}
      data-testid="import-watchlist-modal-backdrop"
    >
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-md max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="import-watchlist-modal"
      >
        <div className="px-4 py-3 border-b border-border bg-muted/40 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4 text-violet-500" />
            <p className="text-sm font-bold">Import from my Watchlist</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
            data-testid="import-watchlist-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 py-2 border-b border-border bg-violet-50/40 dark:bg-violet-950/20">
          <p className="text-[11px] text-muted-foreground">
            Check up to <span className="font-bold text-foreground">{remaining}</span> tickers from your private DiviDrip watchlist to add to this community list.
            {' '}Tickers already in another of your community lists are disabled — each ticker may only appear in one community watchlist.
          </p>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-2 pb-24 sm:pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="import-watchlist-empty">
              You don't have any tickers in your private DiviDrip watchlist yet. Star a few stocks from the Dashboard first.
            </div>
          ) : (
            <ul className="space-y-1">
              {items.map((t) => {
                const upper = t.toUpperCase();
                const inOther = usedSet.has(upper);
                const inThis = alreadyHere.has(upper);
                const disabled = inOther || inThis;
                const checked = selected.has(t);
                const hint = inThis
                  ? 'Already in this list'
                  : inOther
                  ? 'Already in another of your community lists'
                  : '';
                return (
                  <li key={t}>
                    <label
                      className={`flex items-center justify-between gap-2 rounded-md px-2 py-2 border ${
                        disabled
                          ? 'border-border bg-muted/40 opacity-60 cursor-not-allowed'
                          : checked
                          ? 'border-violet-400 bg-violet-50 dark:bg-violet-950/40 cursor-pointer'
                          : 'border-border hover:bg-muted/40 cursor-pointer'
                      }`}
                      data-testid={`import-watchlist-row-${upper}`}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggle(t)}
                          className="w-4 h-4 accent-violet-600"
                          data-testid={`import-watchlist-checkbox-${upper}`}
                        />
                        <span className="font-mono font-bold text-sm">{upper}</span>
                      </div>
                      {hint ? (
                        <span className="text-[10px] text-muted-foreground italic">{hint}</span>
                      ) : null}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center justify-between gap-2">
          <p className="text-[11px] text-muted-foreground tabular-nums">
            {selected.size} / {Math.min(remaining, items.length)} selected
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="import-watchlist-cancel">
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-violet-600 hover:bg-violet-700 text-white"
              onClick={handleImport}
              disabled={selected.size === 0}
              data-testid="import-watchlist-submit"
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              Import {selected.size > 0 ? `(${selected.size})` : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
